import React, { useState, useContext, useEffect } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useFormik, FormikValues } from 'formik'
import TextField from '@mui/material/TextField'
import schema from './schema'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import { DropEvent, FileRejection } from 'react-dropzone'
import api from '../../lib/api'
import { SimpleSnackbarContext } from '../SimpleSnackbar'
import Droparea from './Droparea'
import { CollectionCreateEventRequest, CollectionTheme, ImageType, InviteType } from '../../lib/types'
import { CircularProgress } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import collectionTypes from '../../lib/collectionTypes.json'
import { CreateEventContext } from './Context'
import useShareInvite from '../hooks/useShareInvite'
import ListItemText from '@mui/material/ListItemText'
import { UserContext } from '../UserProvider'

const CreateEvent: React.FC<{ initialTitle: string; themes: CollectionTheme[] }> = ({ initialTitle, themes }) => {
  const handleShare = useShareInvite()
  const { actions } = useContext(CreateEventContext)
  const { actions: snackActions } = useContext(SimpleSnackbarContext)
  const [progress, setProgress] = useState<number>(0)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [templates, setTemplates] = useState<InviteType[]>([])
  const { user } = useContext(UserContext)
  const isNotEnterprise = user?.tier < 4

  useEffect(() => {
    api.getTemplates().then(res => {
      setTemplates(res)
    })
  }, [])

  const onSubmit = async ({ title, cover, description, type, template, themeId }: FormikValues) => {
    try {
      const theme = !isNotEnterprise && themes.find(t => t.id === themeId) ? themes.find(t => t.id === themeId) : {}
      const collectionRequest = { title, coverId: cover?.id, description, type, theme }
      const data: CollectionCreateEventRequest = { collectionRequest: collectionRequest, collectionInviteId: template }
      const collection = await api.createEvent(data)
      snackActions.open('Event created successfully!')
      if (collection.collectionInvite) {
        handleShare(collection.collectionInvite, false, collection.collectionInvite.url, true, false, false, '')
      }
      actions.close(collection)
    } catch (e) {
      snackActions.open('Ops! Something went wrong, please try again!')
      console.error(e)
    }
  }

  const initialValues: {
    title: string
    description: string
    cover: ImageType | null
    type?: string
    template?: string | null
    themeId?: string
  } = {
    title: initialTitle,
    description: ' ',
    type: '',
    cover: null,
    template: null,
    themeId: themes.find(t => t.standard === true)?.id
  }

  const { isSubmitting, isValid, values, setFieldValue, handleBlur, handleChange, touched, errors, handleSubmit } =
    useFormik({
      initialValues,
      validationSchema: schema,
      onSubmit
    })

  useEffect(() => {
    if (initialTitle) {
      setFieldValue('title', initialTitle)
    }
  }, [initialTitle])

  const onDrop: <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[], event: DropEvent) => void =
    async acceptedFiles => {
      try {
        const selectedFile = acceptedFiles[0]
        const image = await api.uploadImage(selectedFile, event => {
          setProgress(Math.round((event.loaded * 100) / event.total))
        })
        setProgress(0)
        await setFieldValue('cover', image)
      } catch (error) {
        snackActions.open('Something was wrong! please try again.')
        setProgress(0)
      }
    }

  return (
    <form>
      <Dialog
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            actions.close()
          }
        }}
        open
      >
        <DialogTitle>
          New event
          <IconButton
            aria-label="close"
            onClick={() => actions.close()}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Add an event allows you to create a collection and a story request in a single step. Simply fill in the
            fields below and press Create. Rakonto will add a new collection, create a new story request and generate a
            unique QR code and link automatically for you.
            <br />
            <br />
            <br /> We recommend you upload an image with 16:9 aspect ratio and a minimum resolution of 720x540.
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 322,
              position: 'relative',
              margin: '16px 0'
            }}
          >
            {!progress && !values.cover && <Droparea onDrop={onDrop} />}
            {values.cover && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 322,
                  backgroundImage: `url(${values.cover.thumbnailUrl})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                <Box
                  onClick={() => setFieldValue('cover', null)}
                  sx={{
                    position: 'absolute',
                    top: '5%',
                    right: '0',
                    width: 40,
                    height: 40,
                    borderRadius: 50,
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <DeleteIcon
                    sx={{
                      color: 'common.black'
                    }}
                  />
                </Box>
              </Box>
            )}
            {!!progress && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
              >
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress variant="determinate" value={progress} />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="caption" component="div" color="text.secondary">{`${Math.round(
                      progress
                    )}%`}</Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          <TextField
            key={'type'}
            name={'type'}
            fullWidth
            select
            label={'Collection type'}
            margin="dense"
            placeholder="Select a subject type for your collection"
            onBlur={handleBlur}
            value={values.type}
            onChange={handleChange}
            error={touched.type && Boolean(errors.type)}
            helperText={(touched.type && errors.type) || ' '}
          >
            <ListSubheader>{collectionTypes[0].name}</ListSubheader>
            {collectionTypes[0].options.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
            <ListSubheader>{collectionTypes[1].name}</ListSubheader>
            {collectionTypes[1].options.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          {!isNotEnterprise && (
            <TextField
              key={'themeId'}
              name={'themeId'}
              fullWidth
              select
              margin="dense"
              variant="outlined"
              placeholder="Select a theme"
              label="Select a theme"
              value={values.themeId}
              onChange={handleChange}
              defaultValue={themes.find(t => t.standard)?.id}
              sx={{ marginBottom: '24px' }}
            >
              <MenuItem value={''}>
                <ListItemText primary={'None'} />
              </MenuItem>
              {themes.map(option => (
                <MenuItem key={option.id} value={option.id}>
                  <ListItemText primary={option?.properties?.themeName} />
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            key={'title'}
            name={'title'}
            fullWidth
            label={'Collection title'}
            margin="dense"
            placeholder="Type your own Collection title"
            onBlur={handleBlur}
            value={values.title}
            onChange={handleChange}
            error={touched.title && Boolean(errors.title)}
            helperText={(touched.title && errors.title) || ' '}
          />
          <TextField
            key={'description'}
            name={'description'}
            fullWidth
            multiline
            rows={4}
            label={'About'}
            placeholder="Type a description here..."
            margin="dense"
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.description && Boolean(errors.description)}
            helperText={(touched.description && errors.description) || ' '}
          />
          <TextField
            key={'template'}
            name={'template'}
            fullWidth
            select
            label={'Request templates'}
            margin="dense"
            placeholder="Select a subject type for your collection"
            onBlur={handleBlur}
            value={templates.find(template => template.id === values.template)?.id ?? ''}
            onChange={handleChange}
          >
            {templates.length > 0 ? (
              templates.map(template => (
                <MenuItem key={template.id} value={template.id}>
                  {template.title}
                </MenuItem>
              ))
            ) : (
              <ListSubheader>
                You have no request templates. Press Create and then follow the steps to create your template and
                generate your link and QR code
              </ListSubheader>
            )}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => actions.close()} sx={{ mt: 1, mr: 1 }}>
            Cancel
          </Button>
          <Button
            disabled={isSubmitting || !isValid || !!progress}
            variant="contained"
            onClick={() => handleSubmit()}
            sx={{ mt: 1, mr: 1 }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  )
}

export default CreateEvent
